import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Rol, Usuario } from '../models';

interface TokenResponse {
  token: string;
  refresh_token: string;
  expires_in: number;
  // devise-api devuelve role/name/company en el nivel superior, no dentro
  // de resource_owner (ver lib/devise/api/responses/token_response_decorator.rb)
  resource_owner: { id: string; email: string };
  role: Rol;
  roles_extra?: Rol[];
  name: string;
  company_id: string;
  company_name: string;
}

const STORAGE_KEY = 'bebidas.session';
export const URL_LOGIN = '/sign_in';
export const URL_REFRESCO = '/tokens/refresh';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  private session = signal<TokenResponse | null>(this.restore());
  /** Una sola petición de refresco para todas las que fallen a la vez con 401. */
  private refrescoEnCurso: Promise<string | null> | null = null;

  readonly usuario = computed<Usuario | null>(() => {
    const s = this.session();
    if (!s) return null;
    return {
      id: s.resource_owner.id,
      email: s.resource_owner.email,
      name: s.name,
      role: s.role,
      roles_extra: s.roles_extra ?? [],
      company_id: s.company_id,
      company_name: s.company_name,
    };
  });
  readonly token = computed(() => this.session()?.token ?? null);
  readonly autenticado = computed(() => this.token() !== null);
  readonly rol = computed<Rol | null>(() => this.usuario()?.role ?? null);

  async login(email: string, password: string): Promise<void> {
    const res = await firstValueFrom(
      this.http.post<TokenResponse>(URL_LOGIN, { email, password }),
    );
    this.guardar(res);
  }

  /**
   * Pide un token nuevo con el refresh_token. Devuelve el token o null si no
   * se pudo (sin refresh_token, caducado o revocado).
   */
  refrescar(): Promise<string | null> {
    this.refrescoEnCurso ??= this.pedirRefresco().finally(() => { this.refrescoEnCurso = null; });
    return this.refrescoEnCurso;
  }

  private async pedirRefresco(): Promise<string | null> {
    const actual = this.session();
    if (!actual?.refresh_token) return null;
    try {
      const res = await firstValueFrom(this.http.post<Partial<TokenResponse>>(URL_REFRESCO, {}, {
        headers: new HttpHeaders({ Authorization: `Bearer ${actual.refresh_token}` }),
      }));
      if (!res?.token) return null;
      // Si mientras tanto se cerró sesión, no la resucitamos.
      if (this.session() !== actual) return null;
      this.guardar({ ...actual, ...res } as TokenResponse);
      return res.token;
    } catch {
      return null;
    }
  }

  logout(): void {
    this.session.set(null);
    localStorage.removeItem(STORAGE_KEY);
    // Datos del repartidor (reparto cacheado, firmas encoladas) no deben quedar en un dispositivo compartido.
    // La cola de entregas no se borra al salir: cada entrega recuerda su usuario y se envía cuando él vuelve a entrar.
    Object.keys(localStorage).filter((k) => k.startsWith('offline.cache.')).forEach((k) => localStorage.removeItem(k));
    localStorage.removeItem('bebidas.company');
    this.router.navigateByUrl('/login');
  }

  /**
   * Sesión caducada (el refresco también falló). A diferencia del logout
   * voluntario, conserva la cola offline: son entregas ya hechas (con firma y
   * foto) que se envían en cuanto el repartidor vuelva a entrar.
   */
  sesionCaducada(): void {
    if (!this.session()) return;
    this.session.set(null);
    localStorage.removeItem(STORAGE_KEY);
    Object.keys(localStorage).filter((k) => k.startsWith('offline.cache.')).forEach((k) => localStorage.removeItem(k));
    const actual = this.router.url;
    const volver = actual && !actual.startsWith('/login') && actual !== '/' ? actual : null;
    void this.router.navigate(['/login'], volver ? { queryParams: { returnUrl: volver } } : {});
  }

  private guardar(res: TokenResponse): void {
    this.session.set(res);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(res));
  }

  /** Rol principal o cualquiera de los extra; el admin lo ve todo. */
  tieneRol(...roles: Rol[]): boolean {
    const u = this.usuario();
    if (!u) return false;
    const mios = [u.role, ...(u.roles_extra ?? [])];
    return mios.includes('admin') || roles.some((r) => mios.includes(r));
  }

  private restore(): TokenResponse | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as TokenResponse) : null;
    } catch {
      return null;
    }
  }
}
